import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/hooks/useApiClient';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ToolAliasApiClient } from '@/features/traceability/api/ToolAliasApiClient';
import { ReferenceAliasContext, type ReferenceAliasContextValue } from './ReferenceAliasContext';

interface Props {
  children: React.ReactNode;
}

export function ReferenceAliasProvider({ children }: Props) {
  const http = useApiClient();
  const { tokenService } = useAuth();
  const companyName = tokenService?.getCompany() ?? undefined;
  const isAuthenticated = !!tokenService?.getAccessToken();

  const [aliasMap, setAliasMap] = useState<Record<string, string>>({});
  const [displayMode, setDisplayMode] = useState<'reference' | 'alias'>('reference');

  // Track whether we've loaded at least once to avoid flicker
  const loadedRef = useRef(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!http || !isAuthenticated) return;

      const client = new ToolAliasApiClient(http);

      void Promise.all([
        client.aliasReadAll(companyName, signal ? { signal } : undefined),
        client.aliasSettingsRead(companyName, signal ? { signal } : undefined),
      ])
        .then(([aliasesResult, settingsResult]) => {
          if (aliasesResult.ok) {
            const map: Record<string, string> = {};
            for (const a of aliasesResult.data.toolAliases) {
              map[a.reference] = a.alias;
            }
            setAliasMap(map);
          }
          if (settingsResult.ok) {
            const mode = settingsResult.data.displayMode;
            if (mode === 'alias' || mode === 'reference') {
              setDisplayMode(mode);
            }
          }
          loadedRef.current = true;
        })
        .catch(() => {
          // Silently ignore errors — never block the app
        });
    },
    [http, companyName, isAuthenticated]
  );

  // Load on mount and whenever http changes
  useEffect(() => {
    if (!http) return;
    const controller = new AbortController();
    load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [http, load]);

  const refresh = useCallback(() => {
    load();
  }, [load]);

  const resolve = useCallback(
    (reference: string) => {
      const alias = aliasMap[reference];
      const hasAlias = !!alias;
      const label = displayMode === 'alias' && hasAlias ? alias : reference;
      return { label, canonical: reference, alias, hasAlias };
    },
    [aliasMap, displayMode]
  );

  const aliasFor = useCallback(
    (reference: string) => aliasMap[reference],
    [aliasMap]
  );

  const value: ReferenceAliasContextValue = {
    displayMode,
    resolve,
    aliasFor,
    refresh,
  };

  return (
    <ReferenceAliasContext.Provider value={value}>
      {children}
    </ReferenceAliasContext.Provider>
  );
}
